import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Alert, Spinner, Button, Modal } from 'react-bootstrap';

export default function Settings() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to view settings');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setError('Failed to load user profile');
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Network error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/me', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Failed to delete account');
      }

      // Logout locally
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Send them to home (or login)
      window.location.href = '/';
    } catch (e) {
      setDeleteError(e.message || 'Error deleting account');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading settings...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="my-4">
        <Alert variant="warning">
          <h4>No User Data</h4>
          <p>Unable to load user information.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h3 className="mb-0">Account Settings</h3>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-4">
                <div style={{ 
                  width: '100px', 
                  height: '100px', 
                  borderRadius: '50%', 
                  backgroundColor: '#007bff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '2.5rem',
                  color: 'white'
                }}>
                  {user?.username?.charAt(0).toUpperCase()}
                </div>
                <h4>{user?.username}</h4>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Username:</strong>
                </div>
                <div className="col-sm-8">
                  {user?.username || 'Not available'}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Email:</strong>
                </div>
                <div className="col-sm-8">
                  {user?.email || 'Not available'}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Member Since:</strong>
                </div>
                <div className="col-sm-8">
                  {user?.createdAt ? formatDate(user.createdAt) : 'Not available'}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>User ID:</strong>
                </div>
                <div className="col-sm-8">
                  <code className="text-muted">{user?._id || 'Not available'}</code>
                </div>
              </div>

              <hr />

              {deleteError && (
                <Alert variant="danger" className="mt-3">
                  {deleteError}
                </Alert>
              )}

              <div className="mt-3">
                <Card className="border-danger">
                  <Card.Body>
                    <h5 className="text-danger mb-2">Danger Zone</h5>
                    <p className="text-muted mb-3">
                      Deleting your account will permanently remove your profile and all your reviews.
                    </p>
                    <Button
                      variant="danger"
                      onClick={() => setShowDeleteModal(true)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete My Account'}
                    </Button>
                  </Card.Body>
                </Card>
              </div>

              <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                  <Modal.Title>Delete Account?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  This will permanently delete your account and all your reviews. This cannot be undone.
                  <br /><br />
                  Are you sure?
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleDeleteAccount} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Yes, Delete'}
                  </Button>
                </Modal.Footer>
              </Modal>
              
              <div className="text-center">
                <p className="text-muted mb-0">
                  <small>
                    Account information is read-only. Contact support if you need to make changes.
                  </small>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
} 